/**
 * WHICH OPTIONS HAVE A PICTURE OF THEIR OWN (Swe Sosetta run 3, panel S5, 2026-10-26).
 *
 * On run 3 the makrouta's Nutella, Dubai and Lotus all led with the same blurry chat photo — a MIXED shot of Kinder
 * and Dubai pieces — and the cookies' four flavours without a photo led with one shared shot. As thumbnails they read
 * "this is what Lotus looks like", and a tap on one swapped the studio picture for the mixed photo.
 *
 * A value has a picture of its own when the picture it leads with is not the one another value leads with. The
 * product page payload says which photos the OWNER put on each card (`mainVariants[].images`, the first choice's
 * values), so that is read where it exists; elsewhere the picture each value's rows lead with
 * (`variants[].images`, already resolved by the server) is compared instead. A value with no picture at all has none
 * of its own either.
 *
 * Such values are drawn as words, not photos, and choosing one keeps the product's main picture in front.
 */

import { slotChoice } from './shopperText';

const urlOf = (image) => (image && typeof image.url === 'string' ? image.url.trim() : '');

/**
 * A COLOUR IS DRAWN AS ITS COLOUR WHEN IT HAS NO PHOTO (round C: S fix round 1, #2). Round C's S2 gave every photo-less
 * value the product's first picture — right for a bakery's flavours and designs, wrong for a colour: DK's black bag
 * showed "Red" under the black photo. A choice whose type is COLOR (the choice behind the slot, `valueType`, never the
 * page's "no type means swatch" default) draws a photo-less value's swatch, and a value that shares a photo with
 * another colour keeps that photo. TEXT choices keep the product's picture.
 */
export function isColourChoice(product, slot) {
  return slotChoice(product, slot)?.valueType === 'COLOR';
}

/** Whether a value has a photo from its own source.
 *  - The product page's payload has the owner's cards (`mainVariants`): the card's photos, else its rows' when they are
 *    more than the product's own gallery, which the server stands in for a value with none.
 *  - A CARD payload (the grid's quick add, the shop strip, RecMiniCard) has no cards, and its `images` IS the whole
 *    gallery — a value's own photo is in it too. There a value with a photo on its rows has one: the f087 rule, so the
 *    sheet shows the photo the product page shows (fix round 2, #1). */
function hasPhotoOfItsOwn(product, slot, value) {
  const rows = (Array.isArray(product?.variants) ? product.variants : []).filter((r) => r && r[slot] === value);
  const mains = slot === 'color' && Array.isArray(product?.mainVariants) ? product.mainVariants : [];
  if (!mains.length) return rows.some((r) => (r.images || []).some(urlOf));
  const main = mains.find((m) => m && m.value === value);
  if (main && (main.images || []).some(urlOf)) return true;
  const gallery = new Set((Array.isArray(product?.images) ? product.images : []).map(urlOf).filter(Boolean));
  return rows.some((r) => (r.images || []).some((image) => urlOf(image) && !gallery.has(urlOf(image))));
}

/** value → the picture it leads with ('' for none), in the order the values first appear. */
function leadPictures(product, slot) {
  const leads = new Map();
  // A colour with no photo of its own leads with none: the product's gallery standing in for it is nobody's photo,
  // and must not make the colour that owns it look shared (fix round 1, #2).
  const colour = isColourChoice(product, slot);
  const variants = Array.isArray(product?.variants) ? product.variants : [];
  const mains = slot === 'color' && Array.isArray(product?.mainVariants) && product.mainVariants.length
    ? product.mainVariants
    : null;
  // THE ONE CARD THAT HOLDS THE WHOLE GALLERY SHOWS THE PRODUCT, NOT ITSELF (the ads feed's rule, round C W6): run 5's tart, «مشكل»
  // holding every photo and «نكهة وحدة» none. Not a colour's: a black bag's only photo is the black one (fix round 1, #2).
  const cardUrls = (m) => (m?.images || []).map(urlOf).filter(Boolean);
  const pictured = mains ? mains.filter((m) => cardUrls(m).length) : [];
  const gallery = (Array.isArray(product?.images) ? product.images : []).map(urlOf).filter(Boolean);
  const showsTheProduct = (main) => !colour && pictured.length === 1 && pictured[0] === main && gallery.length > 0 && gallery.every((u) => cardUrls(main).includes(u));
  for (const v of variants) {
    const value = v?.[slot];
    if (!value || leads.has(value)) continue;
    if (colour && !hasPhotoOfItsOwn(product, slot, value)) { leads.set(value, ''); continue; }
    // The card's own first photo where the owner put one there; a catalogue that keeps its photos on the rows (an
    // import gives each variation its own shot) is read from the rows.
    const main = mains ? mains.find((m) => m && m.value === value) : null;
    const own = main ? urlOf((main.images || []).find(urlOf)) : '';
    if (own) { leads.set(value, showsTheProduct(main) ? '' : own); continue; }
    // ON THE PRODUCT PAGE A VALUE WITH NO PHOTO OF ITS OWN LEADS WITH NONE, for every kind of choice (walk 1, 10-28): the rows the
    // server fills with the product's gallery stand in for it, and the builder leads that gallery with the first card's own picture
    // — the makrouta's Nutella, Dubai and Lotus led with Kinder's Light, Kinder's own picture counted as shared, and the page drew
    // pills where round C S2 draws tiles.
    if (mains && !hasPhotoOfItsOwn(product, slot, value)) { leads.set(value, ''); continue; }
    const row = variants.find((r) => r?.[slot] === value && Array.isArray(r.images) && r.images.some(urlOf));
    leads.set(value, row ? urlOf(row.images.find(urlOf)) : '');
  }
  return leads;
}

/** The values on `slot` with no picture of their own — none at all, or the one another value also leads with. */
export function valuesWithoutOwnPicture(product, slot) {
  const leads = leadPictures(product, slot);
  const out = new Set();
  if (leads.size < 2) return out;
  const seen = new Map();
  leads.forEach((url) => { if (url) seen.set(url, (seen.get(url) || 0) + 1); });
  leads.forEach((url, value) => { if (!url || seen.get(url) > 1) out.add(value); });
  return out;
}

/**
 * WHOSE PICTURE IS THIS (Swe Sosetta run 4, round B: S6, 2026-10-27). A value with no picture of its own shows the
 * product's pictures — run 4's makrouta Nutella, Dubai and Lotus all showed the Kinder picture, and the buyer could not
 * tell. The value a picture BELONGS to is the one value on `slot` that has pictures of its own (the owner's card photos,
 * else its rows') and counts this one among them; null when none does, or more than one.
 */
export function pictureOwner(product, slot, url) {
  const target = typeof url === 'string' ? url.trim() : '';
  if (!target || !product) return null;
  const variants = Array.isArray(product.variants) ? product.variants : [];
  const values = Array.from(new Set(variants.map((v) => v?.[slot]).filter(Boolean)));
  const mains = slot === 'color' && Array.isArray(product.mainVariants) ? product.mainVariants : [];
  const cardPhotos = (value) => {
    const main = mains.find((m) => m && m.value === value);
    return main ? (main.images || []).map(urlOf).filter(Boolean) : [];
  };
  let owners;
  if (values.some((value) => cardPhotos(value).length)) {
    // The owner's card photos say it: a card with no photos of its own owns none (its rows carry the product's).
    owners = values.filter((value) => cardPhotos(value).includes(target));
  } else {
    const shared = valuesWithoutOwnPicture(product, slot);
    owners = values.filter((value) => !shared.has(value)
      && variants.some((r) => r?.[slot] === value && (r.images || []).map(urlOf).includes(target)));
  }
  return owners.length === 1 ? owners[0] : null;
}

/**
 * ONE WIDGET PER CHOICE (Swe Sosetta run 5, round C: S2, 2026-10-28). Run 5's mini cookies showed one picture tile
 * («ريد فيفليت», the only flavour with a photo of its own) beside two plain pills, out of the owner's order — half a
 * row of photos, half a row of words, and the tile first because the tiles were drawn first. Within one choice every
 * value now uses the same widget: if ANY value on the slot has a picture of its own, every value is a tile; otherwise
 * every value is a pill. A tile for a value with no picture of its own (tilePicture, fix round 1 #2, fix round 2):
 *   - TEXT (a bakery's flavours, designs): the PRODUCT's picture, with its name under it;
 *   - COLOR: swatch — painted from its hex (utils/colourSwatch); a colour sharing a photo with another keeps that photo.
 *
 * `values` are the values the picker shows on that slot. 'tiles' | 'pills'.
 */
export function choiceWidget(product, slot, values) {
  const shared = valuesWithoutOwnPicture(product, slot);
  return (Array.isArray(values) ? values : []).some((value) => !shared.has(value)) ? 'tiles' : 'pills';
}

/**
 * The picture a value's tile shows: its own (the first photo of a row that carries it, as the tiles always drew it),
 * else — a value with no picture of its own — the product's own first picture, never another value's. '' for none.
 *
 * A COLOR choice (fix round 1, #2): a colour that shares its photo with another colour keeps that photo; a colour with
 * no photo of its own gets '' — the caller draws its swatch.
 */
export function tilePicture(product, slot, value, shared = valuesWithoutOwnPicture(product, slot)) {
  const rows = Array.isArray(product?.variants) ? product.variants : [];
  if (!shared.has(value)) {
    const row = rows.find((r) => r?.[slot] === value && Array.isArray(r.images) && r.images.some(urlOf));
    if (row) return urlOf(row.images.find(urlOf));
  }
  if (isColourChoice(product, slot)) return leadPictures(product, slot).get(value) || '';
  const own = (Array.isArray(product?.images) ? product.images : []).find(urlOf);
  if (own) return urlOf(own);
  // A payload with no product-level photo (an import that keeps its photos on the rows): the value's own row's.
  const row = rows.find((r) => r?.[slot] === value && Array.isArray(r.images) && r.images.some(urlOf));
  return row ? urlOf(row.images.find(urlOf)) : '';
}
